import "./LabReportSetupComponent.scss";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import React, {useCallback, useEffect} from "react";
import {Grid} from "@material-ui/core";
import {CommonService} from "../../../../shared/services";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import ChipComponent from "../../../../shared/components/chip/ChipComponent";
import {AttachFile} from "@mui/icons-material";
import DialogComponent from "../../../../shared/components/dialog/dialogComponent";
import FilePreviewerComponent from "../../../../shared/components/file-previewer/filePreviewerComponent";
import ColorPickerComponent from "../../../../shared/components/color-picker/ColorPickerComponent";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import commonService from "../../../../shared/services/common.service";
import {useSearchParams} from "react-router-dom";
import useAsyncState from "../../../../shared/hooks/useAsyncState";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";
import useFetch from "../../../../shared/hooks/useFetch";
import {Skeleton} from "@mui/material";
import APIConfig from "../../../../constants/APIConfig";

interface LabReportSetupComponentProps {
    labID: any;
    cancel: any;
    labDetails: any;
    next: Function;
    prev: Function;
    updateLabStatus?: () => Promise<void>;
}

const LabReportSetupComponent = (props: LabReportSetupComponentProps) => {
    const {cancel, labDetails, labID, next, prev, updateLabStatus} = props;
    const labLogoRef = React.useRef<any>(null);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [themeColor, setThemeColor] = React.useState({
        r: '241',
        g: '112',
        b: '19',
        a: '1',
    });
    const [labAttachmentPreviewOpen, setLabAttachmentPreviewOpen] = React.useState(false);
    const [logo, setLogo] = React.useState<any>(null);
    const [isLabLogoCancelVisible, setIsLabLogoCancelVisible] = React.useState(false);
    const [newLabLogo, setNewLabLogo] = React.useState(false);
    const [searchParams] = useSearchParams();
    const lastCompletedStep = parseInt(searchParams.get("lastCompletedStep") || '0');
    const {loading: isLabActivating, execute} = useAsyncState<[], any>();

    const {
        response: InHouseLabDetails,
        loading: isInHouseLabDetailsLoading,
        fetchData: fetchInHouseLabDetails
    } = useFetch<{ id: string, cliaId: string, name: string }>();

    const getInHouseLabDetails = useCallback(() => {
        fetchInHouseLabDetails({
            url: APIConfig.GET_IN_HOUSE_LAB_DETAILS.URL,
            method: APIConfig.GET_IN_HOUSE_LAB_DETAILS.METHOD,
        })
    }, [fetchInHouseLabDetails])

    const handleLogoUpload = useCallback((fieldName: any, event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = null;
            event.target.files = null;
            return;
        }
        event.target.value = null;
        event.target.files = null;
        setNewLabLogo(true);
        setLogo(file);
    }, []);

    const handleAttachmentPreviewOpen = useCallback(() => {
        setLabAttachmentPreviewOpen(true);
        setIsLabLogoCancelVisible(true);
        // setNewLabLogo(true);
    }, []);

    const handleLabAttachmentDialogClose = useCallback(() => {
        setLabAttachmentPreviewOpen(false);

    }, []);

    const labLogoChangeContinue = useCallback(() => {
        setLabAttachmentPreviewOpen(false);
        setIsLabLogoCancelVisible(false);
        setNewLabLogo(true);
    }, []);

    const generateHTML = useCallback(() => {
        // Dynamic logo and color values
        if (!logo.url) {
            const dynamicLogo = logo;
            const dynamicColor = themeColor;
            const reader = new FileReader();
            reader.onload = () => {
                const dynamicLogoUrl = reader.result;
                const newWindow = window.open();
                newWindow?.document.write(commonService._labReportGeneratorService.generateLabReportHtml(dynamicColor, dynamicLogoUrl));
                newWindow?.document.close();
            };
            reader.readAsDataURL(dynamicLogo);
        } else {
            const dynamicLogoUrl = logo.url;
            const newWindow = window.open();
            newWindow?.document.write(commonService._labReportGeneratorService.generateLabReportHtml(themeColor, dynamicLogoUrl));
            newWindow?.document.close();
        }
    }, [logo, themeColor]);

    const handleNext = useCallback(() => {
        if (updateLabStatus) {
            // Call `execute` with the async function and the parameter
            execute(updateLabStatus).then((res) => {
                console.log(res)
                next(); // Call next after the promise resolves
            })
        } else {
            next();
        }
    }, [execute, updateLabStatus, next])

    const handleReportSetupSubmit = useCallback(async () => {
        setIsSubmitting(true);
        let logoFile = logo;
        if (logoFile?.url) {
            logoFile = await commonService.generateBlobFileFromUrl(logoFile?.url, logoFile?.title, logoFile?.mimeType);
        }
        const formData = new FormData();
        formData.append('image', logoFile);
        formData.append('themeColor', commonService.rgbaToHex(themeColor));
        formData.append('lastCompletedStep', lastCompletedStep.toString());
        commonService._lab.addLabReportSetup(labID, formData)
            .then((res: any) => {
                setIsSubmitting(false);
                handleNext();
            })
            .catch((err: any) => {
                console.log(err);
                commonService._alert.showToast(err?.error, "error");
                setIsSubmitting(false);
            })
    }, [logo, themeColor, lastCompletedStep, labID, handleNext]);

    useEffect(() => {
        if (labDetails) {
            setThemeColor(commonService.hexToRgba(labDetails?.themeColor));
            setLogo(labDetails?.logo)
            setNewLabLogo(false)
        }
    }, [labDetails]);

    useEffect(() => {
        getInHouseLabDetails();
    }, [getInHouseLabDetails])

    return (
        <div className={'lab-report-setup-component screen t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Report Setup'}
                handleClick={prev}
                tooltipData={"Customise the laboratory report with a logo and a colour scheme. You can view a sample report to observe the modifications in action."}
            />
            <div className="t-form">
                <div className={'no-form'}>
                    <div className={'t-form-controls-wrapper'}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                <div className='form-sub-title'>Logo*</div>
                                {
                                    logo ? <>
                                        <ChipComponent
                                            label={'Lab Logo'}
                                            onClick={() => {
                                                handleAttachmentPreviewOpen();
                                            }}
                                            onDelete={() => {
                                                setLogo(null);
                                            }}
                                            icon={<AttachFile/>}
                                        />
                                    </> : <>
                                        <input type={"file"}
                                               className={"display-none"}
                                               ref={labLogoRef}
                                               accept={"image/jpg, image/jpeg, image/png"}
                                               onChange={(event) => {
                                                   handleLogoUpload("", event);
                                               }}
                                        />
                                        <ButtonComponent
                                            variant={"outlined"}
                                            fullWidth={false}
                                            color={"secondary"}
                                            handleClick={() => {
                                                labLogoRef.current?.click()
                                            }}
                                            startIcon={<UploadOutlinedIcon/>}
                                            type={'button'}>
                                            Upload
                                        </ButtonComponent>
                                    </>
                                }
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                <div className='form-sub-title'>Theme Color*</div>
                                <ColorPickerComponent onColorChange={(color: any) => {
                                    setThemeColor(color);
                                }}
                                                      canEdit={true}
                                                      themeColor={themeColor}
                                />
                            </Grid>
                        </Grid>
                        <div className={"horizontal-line"}/>
                        <ButtonComponent
                            variant={"outlined"}
                            fullWidth={false}
                            color={"secondary"}
                            disabled={!logo || !themeColor}
                            handleClick={() => {
                                generateHTML()
                            }}
                            type={'button'}
                            startIcon={<RemoveRedEyeIcon/>}
                        >
                            Preview Sample Report
                        </ButtonComponent>
                        {/* add the statement if the if processed lab and test order origin lab is different */}
                        {
                            InHouseLabDetails?.cliaId !== labDetails?.cliaId && (
                                isInHouseLabDetailsLoading ?
                                    <>
                                        <Skeleton className="mrg-top-15 mrg-bottom-15" variant="text" width={'100%'}
                                                  height={80}/>
                                    </> :
                                    <FeedbackComponent className={'mrg-top-15 mrg-bottom-15'}>
                                        This sample was processed at: {InHouseLabDetails?.name}
                                    </FeedbackComponent>
                            )
                        }
                    </div>
                    <div className="form-action-section">
                        <ButtonComponent
                            type="button"
                            color={"primary"}
                            variant={"outlined"}
                            fullWidth={true}
                            disabled={isSubmitting}
                            handleClick={cancel}
                        >
                            Close
                        </ButtonComponent>

                        <ButtonComponent
                            type="button"
                            color={"primary"}
                            variant={"contained"}
                            fullWidth={true}
                            disabled={!logo || !themeColor}
                            handleClick={handleReportSetupSubmit}
                            isLoading={(updateLabStatus && isLabActivating) || isSubmitting}
                        >
                            Submit
                        </ButtonComponent>
                    </div>

                </div>
            </div>
            <DialogComponent open={labAttachmentPreviewOpen}
                             maxWidth={'md'}
                             onClose={() => {
                                 setLabAttachmentPreviewOpen(false)
                             }}
                             isShowCancel={true}
                             title={'Lab Logo Preview'}
                             className={"attachment-preview-dialog"}>

                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    <FilePreviewerComponent
                        attachmentFile={logo}
                        attachmentUrl={logo?.url}
                        isFile={newLabLogo}/>
                </div>
                {logo &&
                    <div className="uploaded-attachment-file-name">
                        {!isLabLogoCancelVisible ? <>
                                <span className="font-weight-bold"> Uploaded File Name:&nbsp;</span>
                                <span>{logo?.name}</span>
                            </> :
                            <></>
                        }
                    </div>
                }
                <div className="attachment-preview-dialog-footer">
                    {
                        isLabLogoCancelVisible ?
                            <ButtonComponent variant={"outlined"}
                                             id="cancel_btn"
                                             fullWidth={true}
                                             handleClick={() => {
                                                 handleLabAttachmentDialogClose();
                                             }}
                            >
                                Cancel
                            </ButtonComponent> :
                            <>
                                <ButtonComponent variant={"outlined"}
                                                 id="re_upload_btn"
                                                 fullWidth={true}
                                                 handleClick={() => {
                                                     labLogoRef.current?.click()
                                                 }}
                                >
                                    Re-upload
                                </ButtonComponent>&nbsp;

                                <ButtonComponent variant={"contained"}
                                                 id="continue_btn"
                                                 fullWidth={true}
                                                 handleClick={labLogoChangeContinue}>
                                    Confirm Upload
                                </ButtonComponent>
                            </>}
                </div>
            </DialogComponent>
        </div>
    );

};

export default LabReportSetupComponent;
